import { useEffect, useRef } from 'react'

/** Fires `onVisible` when the sentinel scrolls into view (infinite scroll). */
export function InfiniteScrollSentinel({
  onVisible,
  disabled,
  className,
  scrollRoot,
  rootMargin = '400px',
}: {
  onVisible: () => void
  disabled?: boolean
  className?: string
  /** Scroll container (e.g. horizontal gallery rail). Defaults to viewport. */
  scrollRoot?: Element | null
  rootMargin?: string
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const busyRef = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el || disabled) return

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || busyRef.current) return
        busyRef.current = true
        Promise.resolve(onVisible()).finally(() => {
          busyRef.current = false
        })
      },
      { root: scrollRoot ?? null, rootMargin },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [onVisible, disabled, scrollRoot, rootMargin])

  return (
    <div ref={ref} className={className} aria-hidden>
      {!disabled ? (
        <p className="py-6 text-center text-xs uppercase tracking-widest text-muted animate-pulse-neon">
          Loading more…
        </p>
      ) : null}
    </div>
  )
}